/**
 * Push Notification Service
 * Handles push notification registration, token management, and event handling
 * Works with Capacitor Push Notifications plugin for iOS and Android
 */

import { isNative, getPlatform } from './platform/platformDetector';

let pushNotificationsPlugin = null;
let appPlugin = null;

// Lazy load Capacitor Push Notifications plugin
const getPushNotifications = async () => {
  if (pushNotificationsPlugin === null && isNative()) {
    try {
      const module = await import('@capacitor/push-notifications');
      pushNotificationsPlugin = module.PushNotifications;
    } catch (e) {
      console.warn('Push Notifications plugin not available:', e.message);
      pushNotificationsPlugin = false;
    }
  }
  return pushNotificationsPlugin || null;
};

// Lazy load App plugin for state checking
const getApp = async () => {
  if (appPlugin === null && isNative()) {
    try {
      const module = await import('@capacitor/app');
      appPlugin = module.App;
    } catch (e) {
      console.warn('App plugin not available:', e.message);
      appPlugin = false;
    }
  }
  return appPlugin || null;
};

// Event listeners
const listeners = {};

/**
 * Emit event to all registered listeners
 */
const emit = (event, data) => {
  if (listeners[event]) {
    listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error(`Error in ${event} listener:`, e);
      }
    });
  }
};

/**
 * Initialize push notifications
 * Should be called once when the app starts
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const initializePushNotifications = async () => {
  if (!isNative()) {
    console.log('[Push] Running in web mode - push notifications not available');
    return { success: false, error: 'Not running in native environment' };
  }

  try {
    const PushNotifications = await getPushNotifications();
    if (!PushNotifications) {
      return { success: false, error: 'Push Notifications plugin not available' };
    }

    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    console.log('[Push] Current permission status:', permStatus.receive);

    // Request permissions if needed
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      const newPermStatus = await PushNotifications.requestPermissions();
      if (newPermStatus.receive !== 'granted') {
        console.warn('[Push] User denied push notification permissions');
        return { success: false, error: 'Permission denied' };
      }
    } else if (permStatus.receive !== 'granted') {
      console.warn('[Push] Push notifications not granted');
      return { success: false, error: 'Permission not granted' };
    }

    // Setup event listeners
    await setupEventListeners(PushNotifications);

    // Register for push notifications
    await PushNotifications.register();
    console.log('[Push] Registration initiated');

    return { success: true };
  } catch (error) {
    console.error('[Push] Initialization error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Setup push notification event listeners
 */
const setupEventListeners = async (PushNotifications) => {
  // Handle successful registration
  await PushNotifications.addListener('registration', async (token) => {
    console.log('[Push] Token received: [REDACTED]');
    
    const deviceToken = {
      token: token.value,
      platform: getPlatform()
    };
    
    // Send token to backend
    try {
      await sendTokenToBackend(deviceToken);
      emit('tokenRegistered', deviceToken);
    } catch (e) {
      console.error('[Push] Failed to send token to backend:', e);
      emit('tokenError', e);
    }
  });

  // Handle registration error
  await PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);
    emit('registrationError', error);
  });

  // Handle notification received (app in foreground)
  await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
    console.log('[Push] Notification received:', notification);
    
    const App = await getApp();
    if (App) {
      const state = await App.getState();
      if (state.isActive) {
        // App is in foreground - emit event for UI handling
        emit('notificationReceived', notification);
      }
    }
  });

  // Handle notification action (user tapped notification)
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[Push] Notification action:', action);
    emit('notificationTapped', action);
  });
};

/**
 * Send device token to backend
 */
const sendTokenToBackend = async (deviceToken) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const authToken = localStorage.getItem('edsupp_token');
  
  if (!authToken) {
    console.warn('[Push] No auth token available, skipping backend registration');
    return;
  }

  const response = await fetch(`${API_URL}/api/push/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify(deviceToken)
  });

  if (!response.ok) {
    throw new Error(`Failed to register token: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export const isPushSupported = () => {
  return isNative();
};

/**
 * Check current permission status
 * @returns {Promise<string>} - 'granted' | 'denied' | 'prompt'
 */
export const checkPermissionStatus = async () => {
  if (!isNative()) {
    return 'denied';
  }

  const PushNotifications = await getPushNotifications();
  if (!PushNotifications) {
    return 'denied';
  }

  const result = await PushNotifications.checkPermissions();
  return result.receive;
};

/**
 * Request push notification permissions
 * @returns {Promise<{success: boolean, status: string}>}
 */
export const requestPermissions = async () => {
  if (!isNative()) {
    return { success: false, status: 'denied' };
  }

  const PushNotifications = await getPushNotifications();
  if (!PushNotifications) {
    return { success: false, status: 'denied' };
  }

  const result = await PushNotifications.requestPermissions();
  return {
    success: result.receive === 'granted',
    status: result.receive
  };
};

/**
 * Subscribe to push notification events
 * @param {string} event - Event name
 * @param {Function} callback - Event handler
 */
export const onPushEvent = (event, callback) => {
  if (!listeners[event]) {
    listeners[event] = [];
  }
  listeners[event].push(callback);
  
  // Return unsubscribe function
  return () => {
    listeners[event] = listeners[event].filter(cb => cb !== callback);
  };
};

/**
 * Unsubscribe from push notification events
 * @param {string} event - Event name
 * @param {Function} callback - Event handler to remove
 */
export const offPushEvent = (event, callback) => {
  if (listeners[event]) {
    listeners[event] = listeners[event].filter(cb => cb !== callback);
  }
};

/**
 * Remove all delivered notifications
 * @returns {Promise<void>}
 */
export const removeAllDeliveredNotifications = async () => {
  if (!isNative()) return;

  const PushNotifications = await getPushNotifications();
  if (PushNotifications) {
    await PushNotifications.removeAllDeliveredNotifications();
  }
};

/**
 * Get list of delivered notifications
 * @returns {Promise<Array>}
 */
export const getDeliveredNotifications = async () => {
  if (!isNative()) return [];

  const PushNotifications = await getPushNotifications();
  if (PushNotifications) {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications || [];
  }
  return [];
};

const pushNotificationService = {
  initialize: initializePushNotifications,
  isSupported: isPushSupported,
  checkPermissions: checkPermissionStatus,
  requestPermissions,
  on: onPushEvent,
  off: offPushEvent,
  removeAllDelivered: removeAllDeliveredNotifications,
  getDelivered: getDeliveredNotifications
};

export default pushNotificationService;
